import { Component, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { GlobalMessageType } from '@boels/models';
import {
  AddToCartModalData,
  AddToCartModalState,
  CartService,
  GlobalMessageFacade,
} from '@boels/shared-state';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { ProductPriceDisclaimerComponent } from '../product-price-disclaimer/product-price-disclaimer.component';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { heightAnimation, ModalService } from '@boels/helpers';
import { catchError, EMPTY, switchMap, take, tap, throwError } from 'rxjs';
import { DialogHeaderComponent, OrderEntryComponent } from '@boels/elements';
import { transition, trigger, useAnimation } from '@angular/animations';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'boels-add-to-cart-modal',
  templateUrl: './add-to-cart-modal.component.html',
  styleUrls: ['./add-to-cart-modal.component.scss'],
  imports: [
    TranslocoModule,
    ReactiveFormsModule,
    ProductPriceDisclaimerComponent,
    DialogHeaderComponent,
    OrderEntryComponent,
    ButtonComponent,
  ],
  providers: [
    CartService,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['addToCart'],
    },
  ],
  animations: [
    trigger('modalEnterAnimation', [
      transition('void => *', [
        useAnimation(heightAnimation, {
          params: { time: '0ms ease-in-out' },
        }),
      ]),
    ]),
  ],
})
export class AddToCartModalComponent {
  /** Injected services */
  #cartService = inject(CartService);
  #router = inject(Router);
  #globalMessageService = inject(GlobalMessageFacade);
  #modalService = inject(ModalService);
  #data = inject<AddToCartModalData>(MAT_DIALOG_DATA);
  autoFocusTarget = signal(this.#data?.productToEdit?.code);

  readonly $loading = signal(true);
  readonly $addToCartState = signal<AddToCartModalState | undefined>(undefined);

  constructor() {
    this.#cartService
      .getProductForAddToCartModal(
        this.#data,
        () => {
          this.$loading.set(false);
        },
        this.#handleErrorAndCloseModal,
      )
      .subscribe((state) => {
        this.$addToCartState.set(state);
        this.$loading.set(false);
      });
  }

  closeModal(): void {
    this.#modalService.close();
  }

  addToCart(): void {
    this.$loading.set(true);
    this.#cartService
      .addToCart(this.$addToCartState())
      .pipe(
        switchMap(() =>
          this.#modalService
            .closeWithCallback()
            .pipe(tap(() => this.#router.navigate(['/cart']))),
        ),
        catchError((error?: Error) => {
          this.#handleErrorAndCloseModal(error);
          return EMPTY;
        }),
        take(1),
      )
      .subscribe();
  }

  recalculateMandatoryAccessoryQuantities(): void {
    if (!this.$addToCartState()) return;
    this.#cartService.recalculateMandatoryAccessoryQuantities(
      this.$addToCartState() as AddToCartModalState,
    );
  }

  deleteOrderEntry(entryNumber: number): void {
    if (!this.$addToCartState()) return;
    this.#cartService.removeOrderEntry(
      this.$addToCartState() as AddToCartModalState,
      entryNumber,
    );
    this.#modalService.close();
  }

  #handleErrorAndCloseModal(error?: Error): void {
    this.$loading.set(false);
    this.#modalService.close();
    if (error) {
      this.#globalMessageService.add(
        { key: 'confirmation.error.text', scope: 'checkout' },
        GlobalMessageType.MSG_TYPE_ERROR,
      );
      const errorMessage = error?.message ?? 'An unknown error occurred.';
      throwError(() => errorMessage);
    }
  }
}
